import React, { useState } from "react";
import { DoubleArrow, MotionPhotosAuto } from "@mui/icons-material";
import {
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  IconButton,
  Paper,
  Card,
  Divider,
  CardContent,
} from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";
import { Tooltip, Drawer } from "@mui/material";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { themes } from "services/constants";

import {
  drawerStyle,
  editIconStyle,
  doubleArrowStyle,
  parentBoxStyle,
  childBoxStyleSpaceBetween,
  stickyHeader,
} from "services/constants";
import {
  CalendarToday as CalendarIcon,
  AccessTime as CalendarClockIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import { infoStyle } from "services/constants";
import { EditSalaryTemplate } from "./EditSalaryTemplate";

const DataTable = ({
  data,
  columns,
  emptyMessage = "No data available",
  renderRow,
  summaryRow,
  showAddWhenEmpty = false,
}) => {
  const safeData = Array.isArray(data) ? data : [];

  return (
    <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor, mb: 2 }}>
      <Table size="small" className="border">
        <TableHead sx={stickyHeader}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={index}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        {safeData.length === 0 ? (
          <TableBody>
            {showAddWhenEmpty ? (
              renderRow(null, 0, true)
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Alert severity="info">{emptyMessage}</Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        ) : (
          <TableBody>
            {safeData.map((item, index) => {
              if (!item) return null;
              return renderRow(item, index);
            })}
            {summaryRow && summaryRow()}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export const ViewSalaryTemplate = ({
  onClose,
  reCallApi,
  getTemplateId,
  screenAccess,
  setHanldeLoader,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const {
    result: templateData,
    loading,
    setLoad: recallTemplateApi,
  } = useFetch(
    `${domain}hrzone/salary/salary_template/salary-template-list/?template_id=${getTemplateId}`,
  );

  const earnings = Array.isArray(templateData?.earnings)
    ? templateData.earnings
    : [];

  const deductions = Array.isArray(templateData?.deduction)
    ? templateData.deduction
    : [];

  const employerContributions = Array.isArray(
    templateData?.employer_contribution,
  )
    ? templateData.employer_contribution
    : [];

  const reimbursements = Array.isArray(templateData?.reimbursement)
    ? templateData.reimbursement
    : [];

  const payrollData = [
    {
      id: 1,
      icon: WalletIcon,
      label: "Calculation Type",
      value: templateData?.salary_type ?? "-",
      iconBgColor: "#09bd711a",
      iconBorderColor: "#09bd71",
      iconColor: "#09bd71",
    },
    {
      id: 2,
      icon: CalendarIcon,
      label: "Salary Basis",
      value: templateData?.salary_basis ?? "-",
      iconBgColor: "#3c73e71a",
      iconBorderColor: "#3c73e7",
      iconColor: "#3c73e7",
    },
    {
      id: 3,
      icon: CalendarClockIcon,
      label: "Salary",
      value: templateData?.total_salary ?? "0",
      iconBgColor: "#bd09091a",
      iconBorderColor: "#d71515",
      iconColor: "#d71515",
    },
  ];

  const SectionHeader = ({ children }) => (
    <Typography sx={{ fontWeight: 600 }}>{children}</Typography>
  );

  const SubHeader = ({ children }) => (
    <Typography {...infoStyle}>{children}</Typography>
  );

  const hanldeEditPanel = () => {
    setIsEditPanelOpen(true);
  };

  const handleStatus = async (status) => {
    const response = await customAPI(
      `${domain}hrzone/salary/salary_template/salary-template-list/`,
      "PUT",
      {
        template_id: templateData?.id,
        status: status,
      },
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      reCallApi(true);
      onClose();
    }
  };

  const handleClosePanel = () => {
    setIsEditPanelOpen(false);
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <Box sx={parentBoxStyle}>
        <Paper elevation={0} sx={{ borderRadius: 0 }}>
          <Box>
            <Box sx={childBoxStyleSpaceBetween}>
              <IconButton onClick={onClose} sx={doubleArrowStyle}>
                <DoubleArrow />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {templateData?.status === "Active" ? (
                  <Tooltip
                    title="Deactivate"
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <IconButton
                      onClick={() => {
                        if (screenAccess) {
                          handleStatus("Inactive");
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to deactivate salary template`,
                          );
                        }
                      }}
                      sx={{
                        color: themes?.redColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                      }}
                    >
                      <PowerSettingsNewIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Activate" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={() => {
                        if (screenAccess) {
                          handleStatus("Active");
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to activate salary template`,
                          );
                        }
                      }}
                      sx={{
                        color: themes?.greenColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                      }}
                    >
                      <MotionPhotosAuto />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                  <IconButton
                    onClick={() => {
                      if (screenAccess) {
                        hanldeEditPanel();
                      } else {
                        showToast(
                          "error",
                          `Access denied: You are not authorized to edit salary template`,
                        );
                      }
                    }}
                    disabled={templateData?.status === "Inactive"}
                    sx={editIconStyle}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ ml: 2.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {templateData?.template_name ?? "Salary Template"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View your predefined salary structure that can be applied to
                employees.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              mt: 4,
              mb: 3,
            }}
          >
            {payrollData.map((item) => {
              const IconComponent = item.icon;
              return (
                <Card
                  key={item.id}
                  sx={{
                    width: 220,
                    height: 123,
                    bgcolor: "#3c73e70d",
                    borderRadius: 1,
                    boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
                    border: 0,
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: item?.id === 3 ? 1 : 2,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 35,
                          height: 35,
                          bgcolor: item.iconBgColor,
                          border: `0.5px solid ${item.iconBorderColor}`,
                          borderRadius: 1,
                          boxShadow: "0px 1px 2px rgba(0,0,0,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1.5,
                        }}
                      >
                        <IconComponent
                          width={16}
                          height={16}
                          style={{ color: item.iconColor }}
                        />
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: "auto",
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#32325D",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Earnings Section */}
          {earnings.length > 0 && (
            <>
              <SectionHeader>Earnings</SectionHeader>
              <SubHeader>
                Add components that contribute to employee earnings (Basic, HRA,
                Bonus, etc.)
              </SubHeader>
              <DataTable
                data={earnings}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component_name ?? "-"}</TableCell>
                    <TableCell>{data?.computation_method ?? "-"}</TableCell>
                    <TableCell>{data?.computation_value ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Earnings</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>₹{templateData?.total_salary ?? 0}</strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Deductions Section */}
          {deductions.length > 0 && (
            <>
              <SectionHeader>Deductions</SectionHeader>
              <SubHeader>
                Add components that are deducted from earnings (TDS, PF, Loan,
                etc.)
              </SubHeader>
              <DataTable
                data={deductions}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component_name ?? "-"}</TableCell>
                    <TableCell>{data?.computation_method ?? "-"}</TableCell>
                    <TableCell>{data?.computation_value ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Deductions</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>₹{templateData?.total_deductions ?? 0}</strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Employer Contribution Section */}
          {employerContributions.length > 0 && (
            <>
              <SectionHeader>Employer Contribution</SectionHeader>
              <SubHeader>
                Add statutory or custom employer contributions
                (PF,ESI,Insurance,etc.)
              </SubHeader>
              <DataTable
                data={employerContributions}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component_name ?? "-"}</TableCell>
                    <TableCell>{data?.computation_method ?? "-"}</TableCell>
                    <TableCell>{data?.computation_value ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Contribution</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>
                        ₹{templateData?.total_employer_contribution ?? 0}
                      </strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Reimbursement Section */}
          {reimbursements.length > 0 && (
            <>
              <SectionHeader>Reimbursement</SectionHeader>
              <SubHeader>
                Add reimbursement components for employee expenses
                (Travel,Food,Medical, etc.)
              </SubHeader>
              <DataTable
                data={reimbursements}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component_name ?? "-"}</TableCell>
                    <TableCell>{data?.computation_method ?? "-"}</TableCell>
                    <TableCell>{data?.computation_value ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Reimbursement</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>₹{templateData?.total_reimbursement ?? 0}</strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Summary Section */}
          <Box
            sx={{
              padding: 2,
              borderRadius: 2,
              width: "100%",
            }}
          >
            {[
              {
                label: "CTC",
                value: templateData?.total_ctc,
              },
              {
                label: "Employer Contribution",
                value: templateData?.total_employer_contribution || "N/A",
              },
              {
                label: "Gross Salary",
                value: templateData?.salary,
              },
              {
                label: "Deduction",
                value: templateData?.total_deductions,
              },
              {
                label: "Net Salary",
                value: templateData?.net_salary,
              },
            ].map((row, i) => (
              <React.Fragment key={i}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                  }}
                >
                  <Typography fontWeight={row.label === "CTC" ? 600 : 500}>
                    {row.label}
                  </Typography>
                  <Typography fontWeight={row.label === "CTC" ? 600 : 500}>
                    ₹{row.value}
                  </Typography>
                </Box>
                {i < 4 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>
      <Drawer
        anchor="right"
        open={isEditPanelOpen}
        onClose={() => {
          handleClosePanel();
        }}
        sx={drawerStyle}
      >
        <EditSalaryTemplate
          onClose={handleClosePanel}
          reCallApi={reCallApi}
          setHanldeLoader={setHanldeLoader}
          templateData={templateData}
          recallTemplateApi={recallTemplateApi}
        />
      </Drawer>
    </>
  );
};
